import { motion, useReducedMotion } from "framer-motion";

import { VIEWPORT, fadeUp, respectReducedMotion } from "./motionPresets";

/** Merge a delay into a variant set's `visible` transition (variant-level
 *  transitions win over the `transition` prop, so it has to go here). */
const withDelay = (variants, delay) => {
  if (!delay) return variants;
  return {
    ...variants,
    visible: {
      ...variants.visible,
      transition: { ...(variants.visible?.transition || {}), delay },
    },
  };
};

/**
 * Scroll-triggered reveal for a single block.
 *
 * <Reveal as="h2" className="..." delay={0.1}>…</Reveal>
 *
 * - `once` by default, so nothing re-animates while the user scrolls back up.
 * - Honours prefers-reduced-motion (fade only, no travel).
 * - Only opacity/transform change, so the element keeps its exact position.
 */
const Reveal = ({
  as = "div",
  children,
  className,
  delay = 0,
  y = 24,
  duration,
  variants,
  viewport,
  onMount = false, // animate immediately instead of on scroll (above-the-fold blocks)
  ...rest
}) => {
  const shouldReduce = useReducedMotion();
  const Tag = motion[as] || motion.div;

  const resolved = withDelay(
    respectReducedMotion(variants || fadeUp(y, duration), shouldReduce),
    delay
  );

  const animationProps = onMount
    ? { animate: "visible" }
    : { whileInView: "visible", viewport: { ...VIEWPORT, ...viewport } };

  return (
    <Tag className={className} initial="hidden" variants={resolved} {...animationProps} {...rest}>
      {children}
    </Tag>
  );
};

export default Reveal;
